import { useCallback, useEffect, useRef, useState } from 'react';

/** A spoken question is short: the recording stops itself at 20 seconds, whatever is happening (the API takes no longer). */
export const VOICE_MAX_MS = 20_000;
/** Shorter than this is a tap, not a question. */
export const VOICE_MIN_MS = 1_000;
/** Once someone has spoken, this much quiet means they are done (the staff recorder's, user decision 2026-10-01). */
export const VOICE_END_SILENCE_MS = 3_000;
/** With no speech at all from the start for this long, nobody is going to speak: it stops without sending. */
export const VOICE_INITIAL_SILENCE_MS = 6_000;
/** How long speech must last, in all, before someone counts as having spoken (a cough or a click does not). */
const SPEECH_START_MS = 250;
/** The first moments, in which the room's own level is measured before anyone speaks. */
const CALIBRATE_MS = 400;
/** The quietest a voice is taken to be (RMS of the waveform, 0–1), however quiet the room. */
const MIN_SPEECH_LEVEL = 0.012;
/** How far above the room's own level a voice is (×2.5 is about 8 dB). */
const SPEECH_OVER_FLOOR = 2.5;
/** Below this share of the speech level counts as quiet: the gap between the two stops flicker at the edge. */
const QUIET_HYSTERESIS = 0.8;
/** Speech needs little: 20 seconds is about 80 KB, far under the API's limit. */
const BITS_PER_SECOND = 32_000;
/** The formats asked for, best first: Opus in WebM (Chrome, Edge, Firefox), AAC in MP4 (Safari), Opus in Ogg. */
const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

/** Why the recorder is not recording: never started or stopped (idle), asking for the microphone, or what stopped it. */
export type RecorderState = 'idle' | 'starting' | 'recording' | 'denied' | 'unavailable' | 'failed';

/** Why a recording stopped by itself: the speaker went quiet, nobody spoke, or the time was up. */
export type AutoStop = 'silence' | 'nothing' | 'limit';

export interface Recording {
   audio: Blob;
   durationMs: number;
   /** Whether anyone was heard; true when the voice could not be measured. */
   heardSound: boolean;
}

interface Session {
   stream: MediaStream;
   recorder: MediaRecorder;
   chunks: Blob[];
   mimeType?: string;
   startedAt: number;
   /** Whether the voice is being measured; when not, nobody is stopped for silence. */
   measured: boolean;
   heard: boolean;
   voicedMs: number;
   quietSince?: number;
   /** The loudness smoothed over about a tenth of a second, so one quiet sample between syllables does not count. */
   smooth?: number;
   /** The room's own level: down at once to any quieter moment, back up only slowly through the quiet. */
   floor: number;
   /** The loudest moment since the meter last asked, 0–1. */
   peak: number;
   lastFrameAt?: number;
   context?: AudioContext;
   frame?: number;
   timer?: number;
}

/** Whether this browser can record here: a secure page (https or localhost) with the microphone API and MediaRecorder. */
export const canRecordVoice = () =>
   typeof window !== 'undefined' && window.isSecureContext !== false && !!navigator.mediaDevices?.getUserMedia && typeof window.MediaRecorder !== 'undefined';

const pickMimeType = () => MIME_TYPES.find((type) => typeof MediaRecorder.isTypeSupported === 'function' && MediaRecorder.isTypeSupported(type));

/** What getUserMedia's refusal means for the guest: they (or the browser, or the page embedding the chat) said no, or there is no microphone. */
const refusal = (error: unknown): RecorderState => {
   const name = (error as { name?: string } | undefined)?.name;
   if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
   if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'NotReadableError') return 'unavailable';
   return 'failed';
};

const recorded = (session: Session, now = Date.now()) => now - session.startedAt;

/** Loudness as the ear hears it, 0–1: about -55 dB is nothing, -10 dB is full. */
const loudness = (rms: number) => Math.min(1, Math.max(0, (20 * Math.log10(Math.max(rms, 1e-6)) + 55) / 45));

const release = (session: Session) => {
   window.clearInterval(session.timer);
   if (session.frame !== undefined) cancelAnimationFrame(session.frame);
   // Stopping every track turns the browser's microphone indicator off
   session.stream.getTracks().forEach((track) => track.stop());
   void session.context?.close().catch(() => undefined);
};

/**
 * Records one spoken question from the microphone: `start` asks for it (the browser prompts the first time) and says
 * how it went, `finish` stops and hands back the recording, `cancel` throws it away. While recording it tells speech
 * from the room's own noise (the room is measured first, and speech must stand well above it, for a quarter of a second
 * in all) and stops by itself, handing the recording to `onAutoStop`: after `VOICE_END_SILENCE_MS` of quiet once someone
 * has spoken, after `VOICE_INITIAL_SILENCE_MS` when nobody has, at `VOICE_MAX_MS`, and when the microphone goes away.
 * The meter reads the loudness on its own (`readLevel`), without a render. The microphone is released whenever
 * recording ends, and when the component leaves.
 */
export function useVoiceRecorder({ onAutoStop }: { onAutoStop?: (recording: Recording, reason: AutoStop) => void } = {}) {
   const [state, setState] = useState<RecorderState>('idle');
   const [elapsedMs, setElapsedMs] = useState(0);
   const session = useRef<Session | null>(null);
   // Each start, and each cancel, moves it on: a start whose microphone arrives after it was called off lets it go
   const attempt = useRef(0);
   const onAutoStopRef = useRef(onAutoStop);
   onAutoStopRef.current = onAutoStop;

   const finish = useCallback((): Promise<Recording | null> => {
      const current = session.current;
      if (!current) return Promise.resolve(null);
      session.current = null;
      const durationMs = recorded(current);
      return new Promise((resolve) => {
         const done = () => {
            release(current);
            setState('idle');
            resolve({
               audio: new Blob(current.chunks, { type: current.recorder.mimeType || current.mimeType || 'audio/webm' }),
               durationMs,
               heardSound: current.heard
            });
         };
         if (current.recorder.state === 'inactive') {
            done();
            return;
         }
         current.recorder.addEventListener('stop', done, { once: true });
         current.recorder.stop();
      });
   }, []);

   const stopBySelf = useCallback(
      (reason: AutoStop) => {
         void finish().then((recording) => recording && onAutoStopRef.current?.(recording, reason));
      },
      [finish]
   );

   const cancel = useCallback(() => {
      attempt.current++;
      const current = session.current;
      if (!current) return;
      session.current = null;
      if (current.recorder.state !== 'inactive') current.recorder.stop();
      release(current);
      setState('idle');
   }, []);

   /** Asks for the microphone and starts recording; resolves to `recording`, or to why it could not. */
   const start = useCallback(async (): Promise<RecorderState> => {
      cancel();
      const mine = attempt.current;
      const calledOff = () => mine !== attempt.current;
      if (!canRecordVoice()) {
         setState('unavailable');
         return 'unavailable';
      }
      setState('starting');
      setElapsedMs(0);

      let stream: MediaStream;
      try {
         stream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 }
         });
      } catch (error) {
         if (calledOff()) return 'idle';
         const refused = refusal(error);
         setState(refused);
         return refused;
      }
      if (calledOff()) {
         stream.getTracks().forEach((track) => track.stop());
         return 'idle';
      }

      const mimeType = pickMimeType();
      let recorder: MediaRecorder;
      try {
         recorder = new MediaRecorder(stream, { ...(mimeType && { mimeType }), audioBitsPerSecond: BITS_PER_SECOND });
      } catch {
         try {
            recorder = new MediaRecorder(stream);
         } catch {
            stream.getTracks().forEach((track) => track.stop());
            setState('failed');
            return 'failed';
         }
      }

      const current: Session = {
         stream,
         recorder,
         chunks: [],
         mimeType,
         startedAt: Date.now(),
         measured: false,
         heard: false,
         voicedMs: 0,
         floor: Number.POSITIVE_INFINITY,
         peak: 0
      };
      recorder.addEventListener('dataavailable', (event) => {
         if (event.data.size > 0) current.chunks.push(event.data);
      });
      // The microphone went away (unplugged, or taken by a call): what was said so far still counts
      stream.getTracks().forEach((track) =>
         track.addEventListener?.('ended', () => {
            if (session.current === current) stopBySelf(current.heard ? 'silence' : 'nothing');
         })
      );

      // Listening for speech: the room is measured first, then a voice is what stands well above it. Where it can't be
      // measured (no Web Audio, or the browser keeps it suspended), anyone is taken as heard and nobody is stopped for
      // silence; the time limit still is.
      try {
         const AudioContextClass = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
         if (!AudioContextClass) throw new Error('No Web Audio');
         const context = new AudioContextClass();
         if (context.state === 'suspended') await context.resume().catch(() => undefined);
         if (calledOff()) {
            void context.close().catch(() => undefined);
            stream.getTracks().forEach((track) => track.stop());
            return 'idle';
         }
         if (context.state !== 'running') {
            void context.close().catch(() => undefined);
            throw new Error('Web Audio suspended');
         }
         const analyser = context.createAnalyser();
         analyser.fftSize = 1024;
         context.createMediaStreamSource(stream).connect(analyser);
         const samples = new Float32Array(analyser.fftSize);
         const measure = () => {
            current.frame = requestAnimationFrame(measure);
            if (session.current !== current) return;
            const now = Date.now();
            const step = current.lastFrameAt === undefined ? 0 : Math.min(250, now - current.lastFrameAt);
            current.lastFrameAt = now;

            analyser.getFloatTimeDomainData(samples);
            let sum = 0;
            for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
            const rms = Math.sqrt(sum / samples.length);
            current.peak = Math.max(current.peak, loudness(rms));
            const level = (current.smooth = current.smooth === undefined ? rms : current.smooth * 0.7 + rms * 0.3);

            // The room is the quietest it gets: someone talking from the first moment still leaves gaps between
            // syllables, which bring the floor down to the room at once
            if (level < current.floor) current.floor = level;
            const elapsed = recorded(current, now);
            if (elapsed < CALIBRATE_MS) return;
            const speechLevel = Math.max(MIN_SPEECH_LEVEL, current.floor * SPEECH_OVER_FLOOR);

            if (level > speechLevel) {
               // Only the moments that are loud themselves count as speech, not the smoothed tail after a click
               if (rms > speechLevel) current.voicedMs += step;
               current.quietSince = undefined;
               if (!current.heard && current.voicedMs >= SPEECH_START_MS) current.heard = true;
            } else if (level < speechLevel * QUIET_HYSTERESIS) {
               current.quietSince ??= now;
               // The room's level drifts up too (a fan starts): follow it through the quiet, slowly
               current.floor += (level - current.floor) * 0.01;
            }

            if (current.heard && current.quietSince !== undefined && now - current.quietSince >= VOICE_END_SILENCE_MS) stopBySelf('silence');
            else if (!current.heard && elapsed >= VOICE_INITIAL_SILENCE_MS) stopBySelf('nothing');
         };
         current.context = context;
         current.measured = true;
         current.frame = requestAnimationFrame(measure);
      } catch {
         current.heard = true;
      }

      current.timer = window.setInterval(() => {
         if (session.current !== current) return;
         const elapsed = recorded(current);
         setElapsedMs(elapsed);
         if (elapsed >= VOICE_MAX_MS) stopBySelf('limit');
      }, 200);

      session.current = current;
      recorder.start();
      setState('recording');
      return 'recording';
   }, [cancel, stopBySelf]);

   /** The loudest moment since the last ask, 0–1, for a meter that draws itself; null when the voice can't be measured. */
   const readLevel = useCallback((): number | null => {
      const current = session.current;
      if (!current?.measured) return null;
      const peak = current.peak;
      current.peak = 0;
      return peak;
   }, []);

   // Leaving stops the recording and frees the microphone
   useEffect(() => cancel, [cancel]);

   return { state, elapsedMs, start, finish, cancel, readLevel };
}
